import { useMemo } from 'react';

import styled from 'styled-components';
import type { ColumnDef } from '@tanstack/react-table';

import { DataTable } from 'components/DataTable';
import { DynamicDeliveryTag } from 'components/DynamicDeliveryTag';
import { EllipsisTooltip } from 'components/EllipsisTooltip';

import { GetDeliveriesQuery } from 'data/__generated__';

import { colors } from 'constants/colors';
import { formatVolumeInLiters, formatWeight } from 'utils/units';
import { formatDateAsAnniversary } from 'helpers/dateTimes';

import { StatusTag } from './components/StatusTag';
import { DELIVERY_STATES_LABELS } from './constants/states';

type DeliveryRow = GetDeliveriesQuery['deliveries'][number];

const DESTINATION_PREFIX_REGEX = /^(STORE|FREEFOOD|PALETTE)\s+/;

// The label embeds the destination as a prefix (e.g. "STORE Balas"); strip it from each
// " + "-joined segment, mirroring the kanban card title.
function cleanDeliveryTitle(label: string): string {
    return label
        .split(' + ')
        .map((segment) => segment.replace(DESTINATION_PREFIX_REGEX, ''))
        .join(' + ');
}

function getDeliveryPersonName(deliveryPerson: DeliveryRow['deliveryPerson']): string {
    if (!deliveryPerson) return '-';
    const { profile, username } = deliveryPerson;
    const fromNames = [profile.firstname, profile.lastname].filter(Boolean).join(' ');
    return profile.fullname || fromNames || username || '-';
}

type DeliveriesDataTableViewProps = {
    deliveries: DeliveryRow[];
    selectedDeliveryIds: string[];
    setSelectedDeliveryIds: React.Dispatch<React.SetStateAction<string[]>>;
};

export const DeliveriesDataTableView = ({
    deliveries,
    selectedDeliveryIds,
    setSelectedDeliveryIds,
}: DeliveriesDataTableViewProps) => {
    const columns = useMemo<ColumnDef<DeliveryRow, unknown>[]>(
        () => [
            {
                id: 'label',
                accessorFn: (row) => cleanDeliveryTitle(row.label),
                header: 'Livraison',
                cell: ({ row }) => (
                    <div className="flex min-w-0 items-center gap-2">
                        <EllipsisTooltip
                            text={cleanDeliveryTitle(row.original.label)}
                            className="max-w-[220px] font-semibold text-foreground"
                        />
                        <DynamicDeliveryTag isDynamic={row.original.isDynamic} label="Livraison dynamique" />
                    </div>
                ),
            },
            {
                accessorKey: 'state',
                header: 'État',
                cell: ({ row }) => <StatusTag state={row.original.state} />,
                sortingFn: (rowA, rowB) =>
                    (DELIVERY_STATES_LABELS[rowA.original.state] || '').localeCompare(
                        DELIVERY_STATES_LABELS[rowB.original.state] || '',
                    ),
            },
            {
                accessorKey: 'deliveryDate',
                header: 'Date de livraison',
                cell: ({ row }) =>
                    row.original.deliveryDate
                        ? formatDateAsAnniversary({ dateTime: new Date(row.original.deliveryDate), displayDay: true })
                        : '-',
            },
            {
                id: 'roundName',
                accessorFn: (row) => row.roundName ?? '',
                header: 'Tournée',
                cell: ({ row }) => {
                    const { roundName, roundColor } = row.original;
                    if (!roundName) return '-';
                    return (
                        <span className="inline-flex items-center gap-1.5">
                            <span
                                className="inline-block size-2.5 shrink-0 rounded-full"
                                style={{ backgroundColor: roundColor || colors.pantinGrey }}
                            />
                            {roundName}
                        </span>
                    );
                },
            },
            {
                id: 'deliveryPerson',
                accessorFn: (row) => getDeliveryPersonName(row.deliveryPerson),
                header: 'Livreur',
            },
            {
                id: 'organizationName',
                accessorFn: (row) => row.organizationName ?? '-',
                header: 'Organisation',
            },
            {
                id: 'site',
                accessorFn: (row) => row.siteName ?? row.totem?.address ?? '-',
                header: 'Site / totem',
            },
            {
                id: 'boxCount',
                accessorFn: (row) => row.contentSummary.boxCount,
                header: 'Caisses',
            },
            {
                id: 'productCount',
                accessorFn: (row) => row.contentSummary.productCount,
                header: 'Produits',
            },
            {
                id: 'totalWeight',
                accessorFn: (row) => row.contentSummary.totalWeight,
                header: 'Poids',
                cell: ({ getValue }) => formatWeight(getValue<number>()),
            },
            {
                id: 'totalVolume',
                accessorFn: (row) => row.contentSummary.totalVolume,
                header: 'Volume',
                cell: ({ getValue }) => formatVolumeInLiters(getValue<number>()),
            },
            {
                id: 'soldoutRefsCount',
                accessorFn: (row) => row.soldoutRefsCount,
                header: 'Ruptures',
                cell: ({ getValue }) => {
                    const count = getValue<number>();
                    return count > 0 ? <span className="font-semibold text-destructive">{count}</span> : '0';
                },
            },
        ],
        [],
    );

    return (
        <TableContainer>
            <DataTable<DeliveryRow>
                columns={columns}
                data={deliveries}
                filterMode="client"
                getRowId={(row) => row._id}
                getRowLink={(row) => `/delivery/${row._id}`}
                defaultSortBy="deliveryDate"
                defaultSortOrder="desc"
                searchPlaceholder="Rechercher dans le tableau…"
                emptyMessage="Aucune livraison."
                enableSelection
                selectedKeys={selectedDeliveryIds}
                onSelectionChange={setSelectedDeliveryIds}
                enableVirtualization
                disableUrlSync
            />
        </TableContainer>
    );
};

const TableContainer = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    min-width: 0;
    overflow: hidden;
`;
